import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import NotifTab from './tabs/NotifTab';
import CalendarTab from './tabs/CalendarTab';
import ManageTab from './tabs/ManageTab';
import SmartSummaryTab from './tabs/SmartSummaryTab';
import SettingsTab from './tabs/SettingsTab';
import FeedbackPanel from './tabs/FeedbackPanel';
import SidePanel from '../util/SidePanel';
import RpPanelWrap from '../util/RpPanelWrap';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

type TabId = 'summary' | 'notif' | 'cal' | 'manage' | 'settings' | 'feedback';

interface RailIcon {
  id: TabId; emoji: string; label: string; iconBg: string; panelTitle: string;
}

const ICON_DEFS: RailIcon[] = [
  { id: 'summary', emoji: '⚡', label: 'Summary',  iconBg: 'rgba(239,68,68,.1)',  panelTitle: 'Smart Summary' },
  { id: 'notif',   emoji: '🔔', label: 'Notif',    iconBg: 'rgba(37,99,235,.1)', panelTitle: 'Notifications' },
  { id: 'cal',     emoji: '📅', label: 'Schedule', iconBg: 'rgba(16,185,129,.1)',panelTitle: "Today's Schedule" },
];
const MANAGE: RailIcon = { id: 'manage', emoji: '⚙️', label: 'Manage', iconBg: 'rgba(100,116,139,.1)', panelTitle: 'Manage panel' };
const SETTINGS: RailIcon = { id: 'settings', emoji: '🛠️', label: 'Settings', iconBg: 'rgba(100,116,139,.1)', panelTitle: 'Settings' };

function loadIcons(): TabId[] {
  try {
    const s = localStorage.getItem('right_rail_icons');
    if (s) return JSON.parse(s);
  } catch {}
  return ['summary', 'notif', 'cal'];
}

interface SettingsProps {
  access: Record<string, any>;
  baseUrl: string;
  refreshUserHeder: () => void;
  is24HourFormat: boolean;
  handleTimeFormatChange: () => void;
  taskOrTasks: boolean;
  handleTaskOrTasks: () => void;
  timeZone: string | null;
  setTimeZone: (tz: string) => void;
  staticDateSelect: any;
  setStaticDateSelect: (v: any) => void;
  unit: any;
  setUnit: (v: any) => void;
}

interface Props {
  userNotice: any[];
  refreshUserHeder: () => void;
  colorTheme: string;
  onThemeChange: (id: string) => void;
  onLogout: () => void;
  settingsProps: SettingsProps;
  userEmail?: string;
}

const RightRail: React.FC<Props> = ({ userNotice, refreshUserHeder, colorTheme, onThemeChange, onLogout, settingsProps, userEmail }) => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [summaryCount, setSummaryCount] = useState(0);

  const fetchSummaryCount = useCallback(async () => {
    try {
      const { data: me } = await axios.get('user');
      let count = 0;
      try {
        const { data: reps } = await axios.get(`time-reports/${me.id}`);
        count += (Array.isArray(reps) ? reps : []).filter(
          (r: any) => r.status === 'PENDING' || r.status === 'pending'
        ).length;
      } catch {}
      try {
        const endTime = new Date();
        const startTime = new Date(endTime.getFullYear(), endTime.getMonth() - 3, 1);
        const { data: expsData } = await axios.post('expenses', { startTime, endTime });
        count += (Array.isArray(expsData?.expenses) ? expsData.expenses : []).filter(
          (e: any) => e.status === 'pending'
        ).length;
      } catch {}
      setSummaryCount(count);
    } catch {}
  }, []);

  useEffect(() => { if (auth) fetchSummaryCount(); }, [auth, fetchSummaryCount]);
  const [icons, setIcons] = useState<TabId[]>(() => {
    const saved = loadIcons();
    if (!saved.includes('summary')) return ['summary', ...saved];
    return saved;
  });

  const notifCount = userNotice?.length || 0;

  const openPanel = (tab: TabId) => {
    if (activeTab === tab && open) { setOpen(false); return; }
    setActiveTab(tab);
    setOpen(true);
  };

  const closePanel = () => setOpen(false);

  const toggleIcon = (id: TabId) => {
    const next = icons.includes(id) ? icons.filter(i => i !== id) : [...icons, id];
    setIcons(next);
    localStorage.setItem('right_rail_icons', JSON.stringify(next));
  };

  const visibleIcons = ICON_DEFS.filter(d => icons.includes(d.id));

  const renderContent = () => {
    if (activeTab === 'summary') return (
      <RpPanelWrap title="Smart Summary" icon="⚡" iconBg="rgba(239,68,68,.1)" onClose={closePanel}>
        <SmartSummaryTab />
      </RpPanelWrap>
    );
    if (activeTab === 'notif') return (
      <RpPanelWrap title="Notifications" icon="🔔" iconBg="rgba(37,99,235,.1)" onClose={closePanel}>
        <NotifTab userNotice={userNotice} refreshUserHeder={refreshUserHeder} />
      </RpPanelWrap>
    );
    if (activeTab === 'cal') return (
      <RpPanelWrap title="Today's Schedule" icon="📅" iconBg="rgba(16,185,129,.1)" onClose={closePanel}>
        <CalendarTab />
      </RpPanelWrap>
    );
    if (activeTab === 'manage') return (
      <RpPanelWrap title="Manage panel" icon="⚙️" iconBg="rgba(100,116,139,.1)" onClose={closePanel}>
        <ManageTab activeIcons={icons} onToggle={toggleIcon} colorTheme={colorTheme} onThemeChange={onThemeChange} />
      </RpPanelWrap>
    );
    if (activeTab === 'settings') return <SettingsTab {...settingsProps} />;
    if (activeTab === 'feedback') return <FeedbackPanel userEmail={userEmail} onClose={closePanel} />;
    return null;
  };

  return (
    <>
      {/* Single SidePanel for all rail tabs — blur backdrop provided by SidePanel */}
      <SidePanel
        open={open}
        onClose={closePanel}
        width={activeTab === 'settings' ? 820 : activeTab === 'feedback' ? 360 : 300}
      >
        {renderContent()}
      </SidePanel>

      <div className="rp-wrap">
        <div className="rail">
          {visibleIcons.map(icon => {
            const isActive = activeTab === icon.id && open;
            const tooltipKey = icon.id === 'summary' ? 'RailSummary' : icon.id === 'notif' ? 'Notification' : 'RailSchedule';
            return (
              <Tooltip key={icon.id} title={t(tooltipKey)} placement="left" arrow>
                <button
                  type="button"
                  className={`r-btn${isActive ? ' active' : ''}`}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => openPanel(icon.id)}
                >
                  {isActive ? '✕' : icon.emoji}
                  {!isActive && icon.id === 'notif' && notifCount > 0 && (
                    <div className="r-btn-badge">{notifCount > 9 ? '9+' : notifCount}</div>
                  )}
                  {!isActive && icon.id === 'summary' && summaryCount > 0 && (
                    <div className="r-btn-badge" style={{ top: 'auto', right: 'auto', bottom: 3, left: 3 }}>{summaryCount > 99 ? '99+' : summaryCount}</div>
                  )}
                </button>
              </Tooltip>
            );
          })}

          <div style={{ flex: 1 }} />
          <div className="r-div" />
          <Tooltip title="Feedback" placement="left" arrow>
            <button
              type="button"
              className={`r-btn${activeTab === 'feedback' && open ? ' active' : ''}`}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => openPanel('feedback')}
            >
              {activeTab === 'feedback' && open ? '✕' : '💬'}
            </button>
          </Tooltip>
          {(settingsProps.access?.settings_enabled === true || settingsProps.access?.settings_view === true || settingsProps.access?.settings_view === 1) && (
            <Tooltip title={t('Settings')} placement="left" arrow>
              <button
                type="button"
                className={`r-btn${activeTab === 'settings' && open ? ' active' : ''}`}
                onMouseDown={e => e.stopPropagation()}
                onClick={() => openPanel('settings')}
              >
                {activeTab === 'settings' && open ? '✕' : '🛠️'}
              </button>
            </Tooltip>
          )}
          <Tooltip title={t('RailManage')} placement="left" arrow>
            <button
              type="button"
              className={`r-btn${activeTab === 'manage' && open ? ' active' : ''}`}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => openPanel('manage')}
            >
              {activeTab === 'manage' && open ? '✕' : '⚙️'}
            </button>
          </Tooltip>
          <div className="r-div" />
          <Tooltip title={t('LogOut')} placement="left" arrow>
            <button
              type="button"
              className="r-btn"
              onMouseDown={e => e.stopPropagation()}
              onClick={onLogout}
              style={{ color: 'rgba(239,68,68,.65)' }}
            >
              🚪
            </button>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default RightRail;
